import api from './api.js';

const TIER_CACHE_TTL_MS = 5 * 60 * 1000;
const tierCache = new Map();
const tierRequests = new Map();

/**
 * Tier Service
 * Manages user tier information and permission checks
 */
export const tierService = {
  /**
   * Get user's tier profile
   * @param {string} userId - User UUID
   * @returns {Promise<Object>} User profile with tier info
   */
  async getUserTier(userId, { force = false } = {}) {
    if (!userId) return null;

    const cached = tierCache.get(userId);
    if (!force && cached && (Date.now() - cached.fetchedAt) < TIER_CACHE_TTL_MS) {
      return cached.profile;
    }

    if (tierRequests.has(userId)) {
      return tierRequests.get(userId);
    }

    const request = (async () => {
      try {
        const response = await api.get(`/user-tier?user_id=${userId}`);
        const profile = response.data.data;
        tierCache.set(userId, { profile, fetchedAt: Date.now() });
        return profile;
      } catch (error) {
        console.error('Error getting user tier:', error);
        return null;
      } finally {
        tierRequests.delete(userId);
      }
    })();

    tierRequests.set(userId, request);

    return request;
  },

  /**
   * Update user's tier (admin only)
   * @param {string} userId - User UUID
   * @param {string} tier - New tier (admin/strategist/creator/basic)
   * @returns {Promise<Object>} Updated profile
   */
  async updateUserTier(userId, tier) {
    try {
      const response = await api.put(`/user-tier?user_id=${userId}`, { tier });
      tierCache.delete(userId);
      return response.data.data;
    } catch (error) {
      console.error('Error updating user tier:', error);
      throw error;
    }
  },

  /**
   * Get all users (admin only)
   * @param {Object} params - Query parameters (tier, page, limit)
   * @returns {Promise<Object>} Users list with pagination
   */
  async getUsers(params = {}) {
    try {
      const response = await api.get('/users', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  },

  // ========== PERMISSION CHECKS ==========

  /**
   * Check if user can access Chat Bot
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canAccessChatBot(userProfile) {
    return ['admin', 'strategist', 'creator'].includes(userProfile?.tier);
  },

  /**
   * Check if user can access Feed
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canAccessFeed(userProfile) {
    // Free+ can access (any authenticated user)
    return userProfile?.tier !== undefined;
  },
  
  /**
   * Check if user can access Strategist features (submit videos, create targets, analyze videos)
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canAccessStrategistFeatures(userProfile) {
    return ['admin', 'strategist'].includes(userProfile?.tier);
  },
  
  /**
   * Check if user can create swipefiles (Pro+)
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canCreateSwipefiles(userProfile) {
    return ['admin', 'strategist', 'creator'].includes(userProfile?.tier);
  },
  
  /**
   * Check if user can follow entities (Pro+)
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canFollowEntities(userProfile) {
    return ['admin', 'strategist', 'creator'].includes(userProfile?.tier);
  },

  /**
   * Check if user can access Creator Panel features (Pro+)
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canAccessCreatorPanel(userProfile) {
    return ['admin', 'strategist', 'creator'].includes(userProfile?.tier);
  },

  /**
   * Legacy: Check if user can access Premium features (deprecated, use canAccessStrategistFeatures)
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canAccessPremiumFeatures(userProfile) {
    return this.canAccessStrategistFeatures(userProfile);
  },

  /**
   * Check if user can manage other users
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canManageUsers(userProfile) {
    return userProfile?.tier === 'admin';
  },

  /**
   * Check if user can edit taxonomy (topics, concepts, etc.)
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canEditTaxonomy(userProfile) {
    return userProfile?.tier === 'admin';
  },

  /**
   * Check if user can access Bot Admin
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canAccessBotAdmin(userProfile) {
    return userProfile?.tier === 'admin';
  },

  /**
   * Check if user can edit Views
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canEditViews(userProfile) {
    return userProfile?.tier === 'admin';
  },

  /**
   * Check if user can access Analytics
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canAccessAnalytics(userProfile) {
    return userProfile?.tier === 'admin';
  },

  /**
   * Check if user can save videos to swipefiles
   * @param {Object} userProfile - User profile object
   * @returns {boolean}
   */
  canSaveVideos(userProfile) {
    // Any logged-in user can save videos to their default swipefile
    return userProfile?.tier !== undefined;
  },

  /**
   * Check if user is public (not logged in)
   * @param {Object} user - User object from auth
   * @returns {boolean}
   */
  isPublicUser(user) {
    return !user;
  },

  /**
   * Get tier display name
   * @param {string} tier - Tier value
   * @returns {string} Display name
   */
  getTierDisplayName(tier) {
    const names = {
      'admin': 'Admin',
      'strategist': 'Strategist',
      'creator': 'Pro',
      'basic': 'Free'
    };
    return names[tier] || 'Unknown';
  },

  /**
   * Get tier badge color classes
   * @param {string} tier - Tier value
   * @returns {string} Tailwind classes
   */
  getTierBadgeClasses(tier) {
    const classes = {
      'admin': 'bg-purple-100 text-purple-800',
      'strategist': 'bg-blue-100 text-blue-800',
      'creator': 'bg-green-100 text-green-800',
      'basic': 'bg-gray-100 text-gray-800'
    };
    return classes[tier] || 'bg-gray-100 text-gray-800';
  },

  /**
   * Check if tier allows route access
   * @param {Object} userProfile - User profile object
   * @param {Array} requiredTiers - Array of required tiers
   * @returns {boolean}
   */
  hasRequiredTier(userProfile, requiredTiers) {
    if (!requiredTiers || requiredTiers.length === 0) {
      return true; // No tier requirement
    }
    return requiredTiers.includes(userProfile?.tier);
  }
};

export default tierService;
